<template>
  <div class="tree-node-card" :class="{ 'tree-node-leaf': isLeaf }">
    <div class="tree-node-header">
      <button type="button" class="tree-node-toggle" @click="toggle">
        <div class="tree-node-title-area">
          <span class="tree-node-level-label">{{ levelLabelUpper }}</span>
          <h3 class="tree-node-title">{{ item.name }}</h3>
        </div>
        <font-awesome-icon
          :icon="['fas', isOpen ? 'chevron-down' : 'chevron-right']"
          class="tree-node-chevron"
        />
      </button>
      <div class="tree-node-actions">
        <button
          v-if="canAddChild && $can('strategic_plan:edit')"
          type="button"
          class="tree-action-btn tree-action-btn-success"
          :title="`Adicionar ${childLevelLabel}`"
          @click.stop="$emit('add-child', item)"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
        <button
          v-if="isLastLevelForActions && $can('action_plan:create')"
          type="button"
          class="tree-action-btn tree-action-btn-success"
          title="Adicionar Ação"
          @click.stop="$emit('add-action-plan', item)"
        >
          <font-awesome-icon :icon="['fas', 'clipboard-list']" />
        </button>
        <button
          type="button"
          class="tree-action-btn"
          title="Visualizar"
          @click.stop="openShowLevel"
        >
          <font-awesome-icon :icon="['far', 'eye']" />
        </button>
        <button
          v-if="$can('strategic_plan:edit')"
          type="button"
          class="tree-action-btn"
          title="Editar"
          @click.stop="$emit('edit-node', item)"
        >
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
        </button>
        <button
          v-if="$can('strategic_plan:delete')"
          type="button"
          class="tree-action-btn tree-action-btn-danger"
          title="Excluir"
          @click.stop="$emit('delete-node', item)"
        >
          <font-awesome-icon :icon="['fas', 'trash']" />
        </button>
      </div>
    </div>

    <div class="tree-node-indicators">
      <span class="indicator-pill" :class="completudeClass">
        <font-awesome-icon :icon="['fas', 'check-square']" class="indicator-pill-icon" />
        {{ aggregados.completudeTexto }}
      </span>
      <span class="indicator-pill" :class="orcamentoClass">
        <font-awesome-icon :icon="['fas', 'dollar-sign']" class="indicator-pill-icon" />
        {{ aggregados.orcamentoTexto }}
      </span>
      <span class="indicator-pill" :class="cronogramaClass">
        <font-awesome-icon :icon="['fas', 'calendar']" class="indicator-pill-icon" />
        {{ aggregados.cronogramaTexto }}
      </span>
      <span v-if="item.indicators_count" class="indicator-pill pill-neutral">
        {{ item.indicators_count }} indicador{{ item.indicators_count > 1 ? 'es' : '' }}
      </span>
      <span v-if="item.measures_count" class="indicator-pill pill-neutral">
        {{ item.measures_count }} meta{{ item.measures_count > 1 ? 's' : '' }}
      </span>
    </div>

    <b-collapse v-model="isOpen">
      <div class="tree-node-body animate-slide-in">
        <TreeNodeCard
          v-for="child in (item.children || [])"
          :key="child.id"
          :item="child"
          :depth="depth + 1"
          :strategic-plan-id="strategicPlanId"
          :expand-all="expandAll"
          :default-open="defaultOpen"
          :search-text="searchText"
          :plan-levels="planLevels"
          @add-child="$emit('add-child', $event)"
          @edit-node="$emit('edit-node', $event)"
          @delete-node="$emit('delete-node', $event)"
          @add-action-plan="$emit('add-action-plan', $event)"
          @action-plan-created="$emit('action-plan-created')"
          @action-item-created="$emit('action-item-created')"
          @reload="$emit('reload')"
        />

        <PlanoAcaoCard
          v-for="(plan, idx) in (item.acoes || [])"
          :key="'ap-' + (plan.id || idx)"
          :objective="item"
          :plan="plan"
          :strategic-plan-id="strategicPlanId"
          :expand-all="expandAll"
          :can-add-action="isLastLevelForActions"
          @action-item-created="$emit('action-item-created')"
        />

        <p v-if="!hasContent" class="tree-node-empty">
          Nenhum item cadastrado neste nível.
        </p>
      </div>
    </b-collapse>
    <ModalShowLevel ref="modalShowLevel" />
  </div>
</template>

<script>
import PlanoAcaoCard from './PlanoAcaoCard.vue';
import ModalShowLevel from './ModalShowLevel.vue';

export default {
  name: 'TreeNodeCard',
  components: { PlanoAcaoCard, ModalShowLevel },
  inject: {
    reloadTree: { default: () => () => {} },
  },
  props: {
    item: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    strategicPlanId: { type: [Number, String], required: true },
    expandAll: { type: Boolean, default: false },
    defaultOpen: { type: Boolean, default: false },
    searchText: { type: String, default: '' },
    planLevels: { type: Array, default: () => [] },
  },
  data() {
    return {
      isOpen: this.defaultOpen || this.expandAll,
    };
  },
  watch: {
    defaultOpen(v) {
      if (!this.expandAll) this.isOpen = v;
    },
    expandAll(v) {
      this.isOpen = v;
    },
  },
  computed: {
    maxDepth() {
      return this.planLevels?.length ?? 1;
    },
    levelLabel() {
      const defaults = ['Nível 1', 'Nível 2', 'Nível 3', 'Nível 4', 'Nível 5'];
      return this.planLevels[this.depth] || defaults[this.depth] || `Nível ${this.depth + 1}`;
    },
    levelLabelUpper() {
      return String(this.levelLabel).toUpperCase();
    },
    childLevelLabel() {
      const defaults = ['Nível 1', 'Nível 2', 'Nível 3', 'Nível 4', 'Nível 5'];
      return this.planLevels[this.depth + 1] || defaults[this.depth + 1] || `Nível ${this.depth + 2}`;
    },
    canAddChild() {
      return this.depth + 1 < this.maxDepth;
    },
    isLastLevelForActions() {
      const levelsCount = this.planLevels?.length ?? 1;
      return this.depth === levelsCount - 1;
    },
    isLeaf() {
      return !this.item.children || this.item.children.length === 0;
    },
    hasContent() {
      const hasChildren = this.item.children && this.item.children.length > 0;
      const hasAcoes = this.item.acoes && this.item.acoes.length > 0;
      return hasChildren || hasAcoes;
    },
    aggregados() {
      const x = this.item.total_acoes_finalizadas ?? 0;
      const y = this.item.total_acoes ?? 0;
      const pct = y > 0 ? Math.round((x / y) * 100) : 0;
      const status = this.item.prazo?.status || 'Sem cronograma';
      const atrasadas = this.item.total_acoes_atrasadas ?? 0;
      const cronogramaTexto =
        status === 'Atrasado' && atrasadas > 0
          ? atrasadas === 1
            ? '1 Atrasada'
            : `${atrasadas} Atrasadas`
          : status;
      return {
        completudeTexto: `${x}/${y} (${pct}%)`,
        orcamentoTexto: this.item.orcamento?.status || 'Sem orçamento',
        cronogramaTexto,
      };
    },
    completudeClass() {
      const x = this.item.total_acoes_finalizadas ?? 0;
      const y = this.item.total_acoes ?? 0;
      if (y === 0 || x === 0) return 'pill-neutral';
      const pct = (x / y) * 100;
      if (pct >= 100) return 'pill-success';
      return 'pill-completude-progress';
    },
    orcamentoClass() {
      const s = (this.item.orcamento?.status || '').toLowerCase();
      if (s.includes('excedeu')) return 'pill-danger';
      if (s.includes('dentro')) return 'pill-success';
      return 'pill-neutral';
    },
    cronogramaClass() {
      const s = (this.item.prazo?.status || '').toLowerCase();
      if (s.includes('atrasad')) return 'pill-danger';
      if (s.includes('no prazo')) return 'pill-success';
      return 'pill-neutral';
    },
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    openShowLevel() {
      this.$refs.modalShowLevel?.show(this.item.id, this.strategicPlanId);
    },
  },
};
</script>

<style scoped>
.tree-node-card {
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  margin-bottom: 0.5rem;
}
.tree-node-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}
.tree-node-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  padding: 0.25rem;
  margin: -0.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.tree-node-toggle:hover {
  background: rgba(0, 0, 0, 0.03);
}
.tree-node-title-area {
  min-width: 0;
  flex: 1;
}
.tree-node-level-label {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted-foreground, #6b7280);
}
.tree-node-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--foreground, #1f2937);
  margin: 0.125rem 0 0 0;
  line-height: 1.375;
}
.tree-node-chevron {
  font-size: 0.875rem;
  color: #9ca3af;
  flex-shrink: 0;
}
.tree-node-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}
.tree-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
}
.tree-action-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #1f2937;
  text-decoration: none;
}
.tree-action-btn-danger:hover {
  background: rgba(var(--negative-rgb), 0.1);
  color: var(--negative);
}
.tree-action-btn-success:hover {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

.tree-node-indicators {
  padding: 0 1.25rem 0.75rem;
  margin-top: -0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.indicator-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}
.indicator-pill-icon {
  font-size: 0.75rem;
}
.pill-success {
  background: var(--status-success-bg);
  color: var(--status-success);
}
.pill-completude-progress {
  background: #dbeafe;
  color: #1d4ed8;
}
.pill-danger {
  background: var(--status-danger-bg);
  color: var(--status-danger);
}
.pill-neutral {
  background: var(--status-neutral-bg);
  color: var(--status-neutral);
}

.tree-node-body {
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
}
.tree-node-empty {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
  padding: 0.5rem 0;
}
</style>
